// ============================================================================
// EL RECORRIDO GUIADO, ANCLADO A LO QUE SE VE (Francisco, 22-09-2026).
//
//     «Que vuelva el recorrido guiado pero bien hecho, que los cuadros se muestren
//      donde deben... que no sea muy tedioso y sea explicativo con lo menos posible».
//     «Antes de arrancar debe haber una ventana que pregunte si quiere el recorrido
//      asistido... además hiciste solo del Inicio, en el resto de pantallas no está».
//
// driver.js apaga la pantalla menos el trozo del que habla y el cuadro sale pegado a ese
// trozo, con una frase. Cada pantalla tiene sus paradas y se enseñan LA PRIMERA VEZ que
// el cliente entra en ella, si aceptó el recorrido: así no son catorce cuadros seguidos,
// son cuatro o cinco en el Inicio, otros tantos la primera vez que abre Nutrición, y así.
// Lo que no esté en pantalla no se cuenta (los pasos se filtran al arrancar), así que
// nunca señala algo que no existe.
//
// Los textos: una idea por parada, dos frases como mucho. Quién decide cuándo sale y
// dónde se apunta: context/OnboardingContext.jsx.
// ============================================================================
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { avisarFinDelRecorrido } from './finDelRecorrido';

//: Las pantallas con recorrido, y por qué ruta se reconoce cada una.
//
// SOLO NUTRICIÓN (Francisco, 23-09: «desactiva el recorrido en todos lados menos en la
// pestaña de nutrición»). Los clientes entran de momento con Nutrición y poco más. Las
// paradas de Inicio, Mi semana y Rutina siguen escritas en PARADAS: para volver a
// encenderlas basta con añadirlas aquí. La primera de la lista es donde sale la entrada
// («Un pequeño tour») y por donde arranca «Ver el recorrido» de Mi perfil.
export const PANTALLAS = ['nutricion'];
export const PRIMERA_PANTALLA = PANTALLAS[0];
export const RUTA_DE = { inicio: '/dashboard', nutricion: '/dashboard/nutrition', semana: '/dashboard/semana', rutina: '/dashboard/routine' };

export const pantallaDe = (pathname) => {
    const p = pantallaDeRuta(pathname);
    return PANTALLAS.includes(p) ? p : null;
};

const pantallaDeRuta = (pathname) => {
    if (pathname === '/dashboard') return 'inicio';
    if (pathname === '/dashboard/nutrition') return 'nutricion';
    if (pathname === '/dashboard/semana') return 'semana';
    if (pathname === '/dashboard/routine') return 'rutina';
    return null;
};

const PARADAS = {
    inicio: [
        {
            // LA FRASE DEL DÍA, LA PRIMERA (Jesús, Word del recorrido del 23-09).
            selector: '[data-testid="frase-del-dia"]',
            titulo: 'La frase del día',
            texto: 'Tu pequeña dosis diaria de reflexión: cuerpo, mente, macros y entreno. Tenemos que trabajarlo todo.',
            side: 'bottom',
        },
        {
            selector: '[data-testid^="dia-banda-"]',
            titulo: 'Tu día, de un vistazo',
            // Vale con el día sin crear («Crear el día») y con el día a medias («Terminar el día»).
            texto: 'Aquí ves cómo va tu día y el botón para dejarlo hecho.',
            side: 'bottom',
        },
        {
            selector: '[data-testid="macros-de-hoy"]',
            titulo: 'Macros · Dieta · Llevas · Falta',
            texto: 'Lo que toca, lo que has creado, lo que llevas comido y lo que te queda. Lo ideal es que cuadren todas las comidas, pero si cuadra el total del día se considera válido.',
            side: 'bottom',
        },
        {
            selector: '[data-testid^="comida-hoy-"]',
            titulo: 'Marca lo que vayas comiendo',
            texto: 'Toca cada comida cuando las comas y los números se actualizan solos.',
            side: 'bottom',
        },
        {
            // Solo a quien tiene intra o post ese día: es lo que ilumina (Word de Jesús, paso 4).
            selector: '[data-testid="comida-hoy-Post"], [data-testid="comida-hoy-Intra"]',
            titulo: 'Tu perientreno',
            texto: 'Lo de durante y después de entrenar va en su propio bloque, y ya está contando en el total del día.',
            side: 'bottom',
        },
        {
            selector: '[data-testid="extras-del-dia"]',
            titulo: '¿Comiste algo que no estaba?',
            texto: 'Apúntalo aquí. Lo tendremos en cuenta cuando toque valorar el día.',
            side: 'top',
        },
        {
            // En el teléfono, el botón «Más» de la barra de abajo; en el ordenador esa barra no
            // existe (`lg:hidden`) y la parada se caía: Inicio perdía su último paso del Word de
            // Jesús (23-09). Allí señala el menú de la izquierda, donde está todo.
            // FIJA: está desde el primer instante, así que encontrarla no dice que la pantalla
            // haya cargado (ver `paradasDeContenido`).
            fija: true,
            selector: '[data-testid="bottomnav-mas"], [data-testid="desktop-sidebar"]',
            titulo: 'Todo lo demás, en «Más»',
            // Seguimiento sube a la barra y Mi rutina pasa a «Más» (26-10).
            texto: 'Tu rutina, Alimentos, Macros, Suplementos, el Asistente y tu Perfil.',
            side: 'top',
        },
    ],
    // EL ORDEN Y LOS TEXTOS DEL WORD DE JESÚS («pantallas onboarding», 01-10): «cambia el orden
    // que pueda ir para atrás también». El paso 1 es la entrada (`preguntarElTour`); aquí van
    // del 2 al 16. Lo que no hay en una cuenta (suplementos, intra y post, favoritas) no se
    // cuenta, y la numeración sigue sin huecos.
    nutricion: [
        {
            selector: '[data-testid="dia-y-tipo"]',
            titulo: 'El día que estás creando',
            texto: 'Cambia de día con las flechas, o pulsa la fecha para verlo en Mi semana.',
            side: 'bottom',
            // (Aquí se pulsaba «Crear el día»; desde el 14-10 el día vacío ya sale abierto.)
        },
        {
            selector: '[data-testid="dia-numeros"]',
            titulo: '¿Cómo llevas tus macros hoy?',
            texto: 'Lo que llevas creado frente a lo que te toca. Marca arriba si es día de entreno o de descanso. Según vas añadiendo alimentos los anillos se llenan; el día queda cuadrado cuando tengas los 3 en verde.',
            side: 'bottom',
        },
        {
            selector: '[data-testid="export-pdf-btn"]',
            titulo: 'Tu dieta en PDF',
            texto: 'Descárgatela para imprimirla o tenerla a mano.',
            side: 'bottom',
        },
        {
            selector: '[data-testid="menu-pantalla"]',
            titulo: 'Copiar, favoritas y más',
            texto: 'Guarda el día como favorito, cópialo a otro día, cambia la configuración o actualiza tus preferencias de alimentos.',
            side: 'bottom',
        },
        {
            // En el teléfono, la primera tarjeta y no la lista entera (la lista ocupa la pantalla).
            // En el ordenador, la columna de las comidas: allí la tarjeta es la comida abierta, y
            // con alimentos medía más que la pantalla (01-10). La columna va antes en la página,
            // y en el teléfono está escondida, así que `visible` coge la que toca en cada ancho.
            selector: '[data-testid="meal-selector"], [data-testid^="meal-card-"]',
            titulo: 'Tus comidas del día',
            texto: 'Cada una con sus correspondientes macros. Tú eliges los alimentos. Tócala para ver lo que hay dentro.',
            side: 'top',
            // Al pasar se abre la primera comida SIN CREAR (las opciones de «Sugiéreme», «Lo hago
            // yo» y «Repetir» solo salen en una comida vacía); si no hay ninguna, la primera.
            luego: () => {
                // En el ordenador, la primera comida vacía de la columna.
                const enColumna = [...document.querySelectorAll('[data-testid^="meal-select-C"]')].filter((e) => e.getBoundingClientRect().height > 0);
                if (enColumna.length) {
                    const vacia = enColumna.find((e) => e.getAttribute('data-vacia') === 'si');
                    if (!vacia) return false;
                    vacia.click();
                    return true;
                }
                const cards = [...document.querySelectorAll('[data-testid^="meal-card-C"]')].filter((e) => e.offsetParent);
                const card = cards.find((c) => /sin crear/i.test(c.innerText || '')) || cards[0];
                if (!card) return false;
                const k = card.getAttribute('data-testid').replace('meal-card-', '');
                const elegir = [...document.querySelectorAll(`[data-testid="meal-select-${k}"]`)].find((e) => e.offsetParent);
                if (elegir) { elegir.click(); return true; }
                const abierta = [...document.querySelectorAll(`[data-testid="build-meal-${k}"], [data-testid="anadir-ingrediente-${k}"]`)]
                    .some((e) => e.offsetParent);
                card.scrollIntoView({ block: 'center' });
                if (abierta) return false;
                const boton = card.tagName === 'BUTTON' ? card : card.querySelector('button');
                if (boton) boton.click();
                return !!boton;
            },
        },
        {
            selector: '[data-testid^="menu-options-"]',
            // El botón ya dice «Sugiéreme un menú»: el título es lo de debajo (Jesús, 01-10, audio).
            titulo: 'La forma rápida de empezar',
            texto: 'Para que no tengas ni que pensar: elige uno de nuestros menús y listo. Menús ya cuadrados con tus macros, listos para usar.',
            side: 'top',
        },
        {
            // Con la comida ya empezada no hay «Lo hago yo»: entonces, «Añadir ingrediente».
            selector: '[data-testid^="build-meal-"], [data-testid^="anadir-ingrediente-C"]',
            titulo: 'Crea tu comida desde cero',
            texto: 'Tú eliges los alimentos y la aplicación te va guiando para ajustar las cantidades a los macros de la comida que corresponda.',
            side: 'top',
        },
        {
            // Desde el rediseño de Enrique (16-10) es una sección con las comidas recientes, y solo
            // sale si hay alguna; con la comida empezada sigue el botón de antes.
            selector: '[data-testid^="repetir-de-otro-dia-"], [data-testid^="repeat-meal-C"]',
            titulo: 'Repite algo que comiste antes',
            texto: 'Otra opción: repetir esta misma comida, pero de otro día.',
            side: 'top',
        },
        {
            // La sección sale siempre, aunque no haya ninguna (Enrique, 16-10); el botón, solo con alguna.
            selector: '[data-testid^="favoritas-de-la-comida-"], [data-testid^="fav-comida-vacia-"]',
            titulo: 'Tus comidas favoritas',
            texto: 'Las que dejaste guardadas como «favoritas». Pueden corresponder a otro momento del día.',
            side: 'top',
        },
        {
            selector: '[data-testid^="mode-auto-"]',
            titulo: 'Automático o manual',
            texto: '<b>Modo Automático</b> te rellena la cantidad del alimento hasta hacer tope, sin pasarte. <b>Modo Manual</b> lo rellenas tú y te puedes pasar (recuerda no exceder el total de macros diario).',
            side: 'bottom',
        },
        {
            // Solo los días de entreno con intra o post. En el ordenador, su pestaña del selector.
            selector: '[data-testid="meal-card-Intra"], [data-testid="meal-card-Post"], [data-testid="meal-select-Intra"], [data-testid="meal-select-Post"]',
            titulo: 'Intra y tu post',
            texto: 'Tu batido intra y tu post. Puedes elegir solo intra, solo post o hacer los dos (te recomiendo lo tercero).',
            side: 'top',
        },
        {
            selector: '[data-testid^="suplementos-comida-"]',
            titulo: 'Tus suplementos',
            texto: 'Lo que te toca tomar en esta comida.',
            side: 'top',
        },
        // DENTRO DE «LO HAGO YO». Van al final: la ventana tapa la pantalla. Se abre al llegar
        // (`abre`), sea cual sea la parada de antes, y se cierra si se vuelve atrás.
        //
        // EL ORDEN Y LO QUE SE VE EN CADA UNA, DE LOS VÍDEOS DE JESÚS (01-10, 22:11 a 23:05):
        // cada paso con su pantalla, no todos sobre la misma. Para enseñarlas, el recorrido toca
        // por el cliente: elige una categoría, abre su preparación y mete un alimento de muestra.
        // Nada se guarda: al terminar o salir, la ventana se cierra como con «Cancelar».
        {
            // Los números Y el buscador (Jesús: «que vea también que está lo de buscar»).
            selector: '[data-testid="montar-arriba"]',
            dentro: true,
            abre: '[data-testid^="build-meal-"], [data-testid^="anadir-ingrediente-C"]',
            titulo: 'Dos maneras de crear una comida',
            texto: 'Puedes buscar por el nombre el alimento si lo tienes claro, o puedes dejar que sea la Calculadora la que te vaya guiando: primero la proteína y después el resto.',
            side: 'bottom',
            // Al pasar se elige una categoría de proteína, para que la siguiente enseñe sus alimentos.
            luego: () => elegirCategoria(),
            espera: '[data-testid^="food-item-"]',
        },
        {
            // La categoría elegida Y su lista, en un solo recuadro (Jesús, vídeo de las 22:48:
            // «tendría que salir esto así»; Francisco, 02-10: «está marcado el listado y no las
            // categorías»). Son dos piezas: `hasta` ilumina desde una hasta la otra.
            selector: '[data-testid="filtros-alimentos"]',
            hasta: '[data-testid="lista-alimentos"]',
            dentro: true,
            titulo: 'Las categorías de proteínas',
            texto: 'Son las primeras que te salen. Toca una y verás los alimentos que, dentro de esa categoría, mejor se ajusten a los macros que tengas en esa comida.',
            side: 'top',
            // Desde el rediseño de «Elijo yo» (16-10) las preparaciones ya se ven debajo de las
            // categorías, sin panel que abrir: esta parada ya no toca nada al pasar.
        },
        {
            // Pantalla que faltaba (Jesús: «esta pantalla te falta»): la categoría y su preparación.
            // Desde el 16-10 las preparaciones están siempre a la vista, bajo las categorías.
            selector: '[data-testid="preparaciones-alimentos"]',
            dentro: true,
            titulo: 'Las formas de preparación',
            texto: 'Debajo de las categorías tienes las formas de preparación: toca una para filtrar la lista.',
            side: 'bottom',
        },
        {
            // La fila entera (el nombre y los dos botones), no solo el nombre: `subir` sube al padre.
            selector: '[data-testid^="food-item-"]',
            dentro: true,
            subir: 1,
            titulo: '80 - 100',
            texto: 'Toca 100 para meter la máxima cantidad posible de ese alimento en esa comida. 80 te la deja al 80%, con lo que tienes margen para añadir otra fuente más.',
            side: 'bottom',
            // Al pasar se mete el primero con su 100: así se ven los pasos de después (el resto de
            // categorías, los ingredientes y «Guardar y cerrar»). Es solo de muestra: no se guarda.
            // Volviendo con «Atrás», el de muestra ya está: pulsarlo otra vez daba el aviso de
            // «ya está en esta comida».
            luego: () => (visible('[data-testid="ingredientes-anadidos"]') ? false : meterProteinaDeMuestra()),
            espera: '[data-testid="ingredientes-anadidos"]',
        },
        {
            selector: '[data-testid="filtros-alimentos"]',
            dentro: true,
            titulo: 'Después, los hidratos',
            texto: 'Cuando cubras el 80% de las proteínas, verás el resto de categorías. Te sugerirá que metas hidratos, pero puedes poner el alimento que quieras.',
            side: 'bottom',
            // CADA PASO CON SU PANTALLA (Jesús, 01-10, vídeos de las 22:50 y 22:56): al pasar se
            // mete un hidrato de muestra, para que la siguiente enseñe ya las preferentes.
            luego: () => (enPreferencias() ? false : meterHidratoDeMuestra()),
            // Envuelta: `enPreferencias` está definida más abajo y aquí aún no existe.
            espera: () => enPreferencias(),
        },
        {
            selector: '[data-testid="filtros-alimentos"]',
            dentro: true,
            titulo: 'Lo último: terminar de cuadrar con tus preferencias',
            texto: 'Cuando cubras el 80% de los hidratos, verás las categorías que tengas marcadas como «preferentes» en tu perfil (esto lo puedes cambiar cuando quieras).',
            side: 'bottom',
        },
        {
            selector: '[data-testid="ingredientes-anadidos"]',
            dentro: true,
            titulo: 'Tus ingredientes',
            texto: 'Abajo vas viendo los ingredientes que añades y puedes modificar la cantidad.',
            side: 'top',
        },
        {
            selector: '[data-testid="save-build-meal"]',
            dentro: true,
            titulo: 'Guarda y cierra',
            texto: 'Cuando tengas la comida lista, guardas y cierras.',
            side: 'top',
        },
        {
            // EL CIERRE (Jesús, 01-10, 22:59): un cuadro en medio, sin nada iluminado. Con «Listo»
            // la ventana de la comida se cierra y se queda en Nutrición.
            sinElemento: true,
            // No dice que la pantalla haya cargado (está siempre): no cuenta para arrancar.
            fija: true,
            dentro: true,
            titulo: 'Y ya estaría',
            texto: 'Te dejo ahora en la pantalla de NUTRICIÓN.',
        },
    ],
    semana: [
        {
            selector: '[data-testid="mi-semana-heading"]',
            titulo: 'Tu semana',
            texto: 'Planifica tu semana con antelación y no falles.',
            side: 'bottom',
        },
        {
            selector: '[data-testid="semana-rango"]',
            titulo: 'Moverte de semana',
            texto: 'Con las flechas ves la anterior o preparas la que viene.',
            side: 'bottom',
        },
        {
            selector: '[data-testid="mi-semana-dias"]',
            titulo: 'Los siete días',
            texto: 'Toca un día para verlo. El porcentaje son las comidas que ya has hecho.',
            side: 'bottom',
        },
        {
            selector: '[data-testid="mi-semana-dia-elegido"]',
            titulo: 'El día elegido',
            texto: 'Sus macros, si entrenas o descansas y cada comida: créala o mírala.',
            side: 'top',
        },
    ],
    rutina: [
        // Con la rutina ya puesta, lo que hay en la pantalla (23-09). Era una sola parada
        // sobre la página entera, y decía «cuando la tengas» a quien ya la tenía.
        {
            selector: '[data-testid="semana-rutina-tira"]',
            titulo: 'Tu semana de entreno',
            texto: 'Qué toca cada día: el entreno o la luna del descanso.',
            side: 'bottom',
        },
        {
            selector: '[data-testid="semana-rutina-hoy"]',
            titulo: 'Lo de hoy',
            texto: 'Cuando termines de entrenar, márcalo aquí.',
            side: 'bottom',
        },
        {
            selector: '[data-testid="semana-rutina-pdf"], [data-testid="routine-pdf-btn"]',
            titulo: 'Tu rutina',
            texto: 'Ábrela para ver tu entrenamiento',
            side: 'bottom',
        },
    ],
};

/** Las paradas de esa pantalla que existen ahora mismo, en su orden. */
//: El primer elemento VISIBLE del selector. Las tarjetas de comida van dos veces en la
//: página (una escondida, la del otro ancho), y `querySelector` devolvía la escondida:
//: la parada se daba por inexistente aunque se viera.
export const visible = (selector) => [...document.querySelectorAll(selector)]
    .find((e) => e.getBoundingClientRect().height > 0) || null;

//: Una parada cuenta si se ve o si se puede abrir al llegar a ella (`abre`: las de dentro de
//: «Lo hago yo», que se abre pulsando ese botón).
const existe = (p) => !!p.sinElemento || !!visible(p.selector) || !!(p.abre && visible(p.abre));

export const paradasEnPantalla = (pantalla) => (PARADAS[pantalla] || []).filter(existe);

//: Cuántas paradas DEL CONTENIDO hay ya pintadas (sin las fijas, como la barra de abajo).
//: Es lo que dice si la pantalla ha terminado de cargar.
export const paradasDeContenido = (pantalla) => paradasEnPantalla(pantalla).filter((p) => !p.fija).length;

/**
 * Arranca el recorrido de una pantalla sobre lo que hay en ella.
 *
 * `alTerminar(estado)` recibe 'visto' (llegó al final) o 'saltado' (Salir, tocar fuera
 * o Escape). Devuelve false si no había nada que enseñar: el que llama decide qué hacer.
 */
//: Pulsa el primer elemento visible de un selector. Para las paradas que abren algo.
const clicEn = (selector) => {
    const el = [...document.querySelectorAll(selector)].find((e) => e.offsetParent);
    if (!el) return false;
    // Si es un botón, él; si es un contenedor (la tarjeta de una comida), su primer botón,
    // que es la cabecera que la abre: el clic en el contenedor no hace nada.
    const boton = el.tagName === 'BUTTON' ? el : el.querySelector('button');
    (boton || el).click();
    return true;
};

//: Elige una categoría de proteína en «Lo hago yo» (Huevos si está), para que salgan
//: alimentos con su 80 y su 100. Si los filtros salen recogidos, despliega antes la barra.
const elegirCategoria = () => {
    const zona = document.querySelector('[data-testid="filtros-alimentos"]');
    if (!zona) return false;
    // Volviendo con «Atrás», los alimentos ya están: no se toca nada.
    if (visible('[data-testid^="food-item-"]')) return false;
    const elegir = () => {
        const chips = [...zona.querySelectorAll('[aria-label]')].filter((e) => e.offsetParent);
        const huevo = chips.find((e) => /huevo/i.test(e.getAttribute('aria-label') || ''));
        const el = huevo || chips[1] || chips[0];
        if (el) el.click();
        return !!el;
    };
    if (elegir()) return true;
    const barra = zona.querySelector('button');
    if (barra) { barra.click(); setTimeout(elegir, 350); }
    return !!barra;
};

//: Los macros de una fila de alimento («P=39 g H=39 g G=1 g»).
const macrosDeLaFila = (fila) => {
    const t = (fila && fila.innerText) || '';
    // «P=23,5» (la lista de antes) o «23,5 P» (la de «Elijo yo» rediseñada, 16-10).
    const n = (k) => {
        const m = t.match(new RegExp(k + '=([\\d.,]+)')) || t.match(new RegExp('([\\d.,]+)\\s*' + k + '\\b'));
        return m ? parseFloat(m[1].replace(',', '.')) : 0;
    };
    return { P: n('P'), H: n('H') };
};

//: Mete con su 100 la proteína de la lista con MENOS hidratos: así se cubre la proteína y los
//: hidratos siguen por cubrir, y el paso de los hidratos enseña su pantalla (Jesús, 01-10).
const meterProteinaDeMuestra = () => {
    const filas = [...document.querySelectorAll('[data-testid^="food-item-"]')].filter((e) => e.getBoundingClientRect().height > 0);
    if (!filas.length) return clicEn('[data-testid^="add-100-"]');
    const mejor = filas.reduce((a, f) => {
        const m = macrosDeLaFila(f);
        const r = m.P > 0 ? m.H / m.P : 99;
        return !a || r < a.r ? { f, r } : a;
    }, null).f;
    const id = mejor.getAttribute('data-testid').replace('food-item-', '');
    return clicEn(`[data-testid="add-100-${id}"]`) || clicEn('[data-testid^="add-100-"]');
};

//: ¿Dice ya la línea de arriba lo de las preferencias?
const enPreferencias = () => /preferencias/i.test((document.querySelector('[data-testid="montar-cabecera"]') || {}).innerText || '');

//: Mete un hidrato de muestra: elige la primera categoría de la pantalla de los hidratos y,
//: cuando salen sus alimentos, el primero con su 100. Se espera a la lista sin tiempo fijo.
const meterHidratoDeMuestra = () => {
    const zona = document.querySelector('[data-testid="filtros-alimentos"]');
    if (!zona) return false;
    const chips = [...zona.querySelectorAll('[aria-label]')].filter((e) => e.getBoundingClientRect().height > 0);
    if (!chips.length) return false;
    chips[0].click();
    const desde = Date.now();
    // Si se sale del recorrido mientras carga la lista, se deja de buscar.
    const miVuelta = vuelta;
    const mirar = () => {
        if (miVuelta !== vuelta || !document.querySelector('[data-testid="montar-cabecera"]')) return;
        if (enPreferencias()) return;
        if (visible('[data-testid^="add-100-"]')) { clicEn('[data-testid^="add-100-"]'); return; }
        if (Date.now() - desde < 8000) setTimeout(mirar, 250);
    };
    setTimeout(mirar, 300);
    return true;
};

//: Ya no prepara nada: lo que había que abrir lo abren las paradas al pasar (`luego`).
export const prepararPantalla = () => false;

//: Lo que driver.js deja en la página si algo se tuerce: la capa oscura, el cuadro y las
//: clases del cuerpo. Se barre al terminar, que la pantalla se quedaba apagada (23-09).
//: MIENTRAS SE ESPERA A LO QUE SE ABRE, LA PANTALLA SIGUE APAGADA (Francisco, 02-10: «a veces
//: tarda en pasar al siguiente paso y parece que se cerró, porque habilita toda la pantalla»).
//: Entre un recorrido y el siguiente no había capa: se veía la app entera y se podía tocar.
const ponerEspera = () => {
    if (document.querySelector('.recorrido-espera')) return;
    const capa = document.createElement('div');
    capa.className = 'recorrido-espera';
    capa.setAttribute('data-testid', 'recorrido-espera');
    capa.innerHTML = '<span>Un momento…</span>';
    document.body.appendChild(capa);
};
const quitarEspera = () => document.querySelectorAll('.recorrido-espera').forEach((e) => e.remove());

//: UN RECUADRO QUE VA DE UNA PIEZA A OTRA (`hasta`). driver.js ilumina un elemento; para dos
//: seguidos se pone uno transparente que los cubre, dentro de su mismo contenedor con scroll
//: (así se mueve con ellos), y se ilumina ese.
let zona = null;
const quitarZona = () => {
    if (!zona) return;
    const { capa, padre, posicion, piezas } = zona;
    capa.remove();
    padre.style.position = posicion;
    piezas.forEach((e) => e.classList.remove('recorrido-con-aire'));
    zona = null;
};
const zonaEntre = (desde, hasta) => {
    quitarZona();
    const a = visible(desde);
    const b = visible(hasta);
    if (!a || !b) return a || b || document.body;
    // El contenedor común más cercano.
    let padre = a.parentElement;
    while (padre && !padre.contains(b)) padre = padre.parentElement;
    if (!padre) return a;
    a.classList.add('recorrido-con-aire');
    b.classList.add('recorrido-con-aire');
    // La lista tiene su propio scroll: se sube arriba del todo, que si no la primera fila
    // quedaba cortada (02-10, en el teléfono). Y el recuadro se coloca con hueco por encima
    // para el cuadro del texto, que así no tapa la lista.
    const scrollDe = (el) => {
        let x = el.parentElement;
        while (x && !/(auto|scroll)/.test(getComputedStyle(x).overflowY)) x = x.parentElement;
        return x;
    };
    const deLaLista = scrollDe(b);
    if (deLaLista && deLaLista !== padre && deLaLista.contains(b)) deLaLista.scrollTop = 0;
    try { a.scrollIntoView({ block: 'start', behavior: 'instant' }); } catch (e) { a.scrollIntoView(); }
    const columna = scrollDe(a);
    // Lo que ocupa el cuadro del texto con sus botones (unos 270 px) y un margen.
    const HUECO_ARRIBA = 300;
    const arriba = a.getBoundingClientRect().top;
    if (columna && arriba < HUECO_ARRIBA) columna.scrollTop -= (HUECO_ARRIBA - arriba);
    const posicion = padre.style.position;
    if (getComputedStyle(padre).position === 'static') padre.style.position = 'relative';
    const capa = document.createElement('div');
    capa.className = 'recorrido-zona';
    capa.setAttribute('data-testid', 'recorrido-zona');
    Object.assign(capa.style, { position: 'absolute', pointerEvents: 'none', left: '0', right: '0' });
    padre.appendChild(capa);
    zona = { capa, padre, posicion, piezas: [a, b], deLaLista: deLaLista && deLaLista !== padre ? deLaLista : null };
    medirZona();
    return capa;
};
//: Coloca el recuadro sobre las dos piezas. Se vuelve a llamar al recolocar: la lista termina
//: de pintarse un momento después y el recuadro se quedaba corto (02-10, «Mostrar más» cortado).
const medirZona = () => {
    if (!zona) return;
    const { capa, padre, piezas: [a, b], deLaLista } = zona;
    const rp = padre.getBoundingClientRect();
    const ra = a.getBoundingClientRect();
    const rb = b.getBoundingClientRect();
    // Hasta donde se ve la lista: si es más alta que su hueco, el recuadro acaba en el borde.
    const fin = deLaLista ? Math.min(rb.bottom, deLaLista.getBoundingClientRect().bottom) : rb.bottom;
    capa.style.top = `${ra.top - rp.top + padre.scrollTop}px`;
    capa.style.height = `${fin - ra.top}px`;
};

const barrer = () => {
    quitarEspera();
    quitarZona();
    document.querySelectorAll('.driver-overlay, .driver-popover').forEach((e) => e.remove());
    document.body.classList.remove('driver-active', 'driver-fade', 'driver-simple');
    document.body.classList.remove('recorrido-en-marcha');
    document.querySelectorAll('.driver-active-element').forEach((e) => {
        e.classList.remove('driver-active-element');
        e.removeAttribute('aria-haspopup');
        e.removeAttribute('aria-expanded');
        e.removeAttribute('aria-controls');
    });
};

//: El elemento de una parada, puesto EN MEDIO de la pantalla antes de iluminarlo (repaso en
//: el teléfono, 23-09: «no está relacionado el recorrido con lo que señala»). driver.js solo
//: desplaza si el elemento está fuera de la ventana, y los que quedaban debajo de la barra
//: de abajo contaban como dentro: el cuadro señalaba un botón que no se veía. Con
//: `recorrido-en-marcha` la página tiene sitio de sobra por abajo para centrar cualquiera.
const enMedio = (selector) => {
    const el = visible(selector);
    if (!el) return document.body;
    try { el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'instant' }); } catch (e) { el.scrollIntoView(); }
    return el;
};

//: LA ENTRADA AL RECORRIDO (Jesús, Word del 23-09: la pregunta «¿Quieres el recorrido
//: asistido?» fuera). Una tarjeta como las del recorrido, en medio y sin nada iluminado:
//: «Antes de empezar · Un pequeño tour». Devuelve true con «Venga, vamos a ello» y false
//: con «Ahora no» o si la cierra.
export const preguntarElTour = () => new Promise((resolver) => {
    let quiere = false;
    const d = driver({
        animate: true,
        allowClose: true,
        overlayClickBehavior: 'close',
        overlayColor: '#000',
        overlayOpacity: 0.8,
        popoverClass: 'recorrido-12en12 recorrido-entrada',
        showProgress: false,
        showButtons: ['previous', 'next'],
        prevBtnText: 'Ahora no',
        nextBtnText: 'Venga, vamos a ello',
        doneBtnText: 'Venga, vamos a ello',
        onPrevClick: () => d.destroy(),
        onNextClick: () => { quiere = true; d.destroy(); },
        onDestroyed: () => { barrer(); resolver(quiere); },
        steps: [{
            popover: {
                // Los textos del Word de Jesús del 01-10 («pantallas onboarding»), paso 1.
                title: '<span class="recorrido-antes">Antes de empezar</span>Un pequeño tour',
                // Negritas de Francisco y Jesús (02-10): el asterisco con «IMPORTANTE» y «NUTRICIÓN».
                // SIN LO DE LA BETA (12-10, F11): decía «de momento es lo único que quiero que
                // pruebes» y que el resto de la app no estaría operativo «hasta dentro de unos
                // días». La app ya está entera; se queda el recorrido y a dónde escribir.
                description: 'Te hago un recorrido rápido por las principales pantallas de la parte de <b>NUTRICIÓN</b>.'
                    + '<br><br><b>*IMPORTANTE:</b> si algo no te funciona, escríbenos por el CHAT. Adjunta capturas de pantalla y explícalo con el mayor detalle posible. El equipo lo revisa y te responde.'
                    + '<br><br>Esto lo puedes repetir cuando quieras desde «Mi Perfil».',
                // «Ahora no» es el botón de «anterior», y driver.js lo desactiva en el primer
                // paso: salía gris y no hacía nada (23-09). Aquí no hay nada desactivado.
                disableButtons: [],
            },
        }],
    });
    d.drive();
});

// EL RECORRIDO NO SOBREVIVE A UN CAMBIO DE PANTALLA (25-09). Si se cambiaba de pantalla con
// él abierto, su capa y su cuadro se quedaban encima de la pantalla nueva, señalando algo que
// ya no estaba y tapando todos los botones: en Mi perfil, el mismo «Ver el recorrido».
// `pararRecorrido` lo quita EN SILENCIO: no lo da por visto ni por saltado, así que esa
// pantalla sigue pendiente y sale la próxima vez que se entre.
let enMarcha = null;
//: Cambia cada vez que se para: lo que estaba esperando a que se pinte algo (al abrir una
//: comida, al volver atrás) mira esto y no relanza un recorrido ya parado.
let vuelta = 0;
export const pararRecorrido = () => {
    vuelta += 1;
    const actual = enMarcha;
    enMarcha = null;
    if (actual) actual.parar();
    barrer();
};

export const arrancarRecorrido = ({ pantalla, alTerminar } = {}) => {
    const todas = PARADAS[pantalla] || [];
    // Si alguna parada abre algo, el total cambia a mitad (Nutrición pasaba de «1 de 5» a
    // «6 de 10»): ahí se cuenta solo el paso, sin total.
    const conTotal = !todas.some((p) => p.luego);
    const primeros = paradasEnPantalla(pantalla);
    // El cierre solo no es un recorrido.
    if (!primeros.some((p) => !p.sinElemento)) return false;
    let terminado = false;
    const terminar = (estado) => {
        if (terminado) return;
        terminado = true;
        barrer();
        // Y si se quedó abierta la ventana de una comida (las últimas paradas van dentro de
        // «Lo hago yo»), se cierra: el recorrido acaba en Nutrición (lib/finDelRecorrido.js).
        avisarFinDelRecorrido(estado);
        if (alTerminar) alTerminar(estado);
    };

    // Un recorrido con estos pasos, empezando en `desde`. Cuando una parada abre algo
    // (`luego`), este se destruye EN SILENCIO y se arranca otro con las paradas recontadas:
    // cambiarle los pasos a uno en marcha dejaba un cuadro y una capa colgados.
    const lanzar = (pasos, desde) => {
        document.body.classList.add('recorrido-en-marcha');
        let recontando = false;
        let llegoAlFinal = false;
        let parado = false;
        // Pulsado «Salir»: el cuadro enseña el aviso y solo se cierra con «Entendido».
        let saliendo = false;
        const d = driver({
            animate: true,
            // Instantáneo a propósito: con el desplazamiento suave el cuadro se posicionaba
            // antes de terminar de desplazar y se pisaba con lo que señalaba.
            smoothScroll: false,
            allowClose: true,
            overlayClickBehavior: 'close',
            overlayColor: '#000',
            // Más oscuro detrás (reunión del 25-09, «más contraste»): lo iluminado resalta más.
            overlayOpacity: 0.9,
            stagePadding: 8,
            stageRadius: 16,
            popoverClass: 'recorrido-12en12',
            popoverOffset: 12,
            showProgress: true,
            progressText: conTotal ? 'Paso {{current}} de {{total}}' : 'Paso {{current}}',
            // «ATRÁS», «SIGUIENTE» Y «SALIR», SIEMPRE (Jesús, 01-10: «que pueda ir para atrás
            // también»). Era «Saltar» y «Siguiente», sin vuelta atrás. «Salir» lo añade
            // `onPopoverRender` y cuenta como saltado.
            showButtons: ['previous', 'next'],
            prevBtnText: 'Atrás',
            nextBtnText: 'Siguiente',
            doneBtnText: 'Listo',
            onPopoverRender: (popover) => {
                const salir = document.createElement('button');
                salir.type = 'button';
                salir.className = 'recorrido-salir';
                salir.textContent = 'Salir';
                salir.setAttribute('data-testid', 'recorrido-salir');
                // «SALIR» AVISA ANTES DE CERRAR (Enrique, 05-11, prueba e2e 5.11): en el mismo cuadro
                // queda solo «Puedes acceder a esto desde Mi perfil» y un botón, «Entendido»,
                // que es el que cierra el recorrido (y lo da por saltado, como antes «Salir»).
                salir.addEventListener('click', () => {
                    saliendo = true;
                    popover.wrapper.classList.add('recorrido-saliendo');
                    [popover.title, popover.progress, popover.footerButtons, salir]
                        .forEach((el) => { if (el) el.style.display = 'none'; });
                    popover.description.style.display = '';
                    popover.description.textContent = 'Puedes acceder a esto desde Mi perfil';
                    const entendido = document.createElement('button');
                    entendido.type = 'button';
                    entendido.className = 'recorrido-entendido';
                    entendido.textContent = 'Entendido';
                    entendido.setAttribute('data-testid', 'recorrido-entendido');
                    entendido.addEventListener('click', () => d.destroy());
                    popover.footer.appendChild(entendido);
                    entendido.focus();
                });
                popover.footer.insertBefore(salir, popover.footer.firstChild);
            },
            onPrevClick: () => {
                if (saliendo) return;   // con el aviso de «Salir», las flechas no mueven nada
                const i = d.getActiveIndex() ?? 0;
                if (i === 0) return;
                // De la primera parada de «Lo hago yo» hacia atrás: se cierra la ventana (como
                // con «Cancelar», el recorrido no mete nada) y se vuelve a la parada de fuera.
                if (pasos[i].dentro && !pasos[i - 1].dentro) {
                    recontando = true;
                    d.destroy();
                    barrer();
                    ponerEspera();
                    avisarFinDelRecorrido('atras');
                    const miVuelta = vuelta;
                    setTimeout(() => { if (miVuelta === vuelta) lanzar(pasos, i - 1); }, 400);
                    return;
                }
                d.movePrevious();
            },
            onNextClick: () => {
                if (saliendo) return;
                const i = d.getActiveIndex() ?? 0;
                const actual = pasos[i];
                const siguiente = pasos[i + 1];
                // Lo que hay que abrir para seguir: lo que abre esta parada al pasar (`luego`) o
                // la ventana donde está la siguiente (`abre`).
                const abrir = actual && actual.luego ? actual.luego
                    : siguiente && siguiente.abre && !visible(siguiente.selector) ? () => clicEn(siguiente.abre)
                        : null;
                if (abrir) {
                    recontando = true;
                    d.destroy();
                    barrer();
                    ponerEspera();
                    const miVuelta = vuelta;
                    let hizo;
                    try { hizo = abrir(); } catch (e) {
                        // Que un fallo al abrir no deje la pantalla apagada para siempre.
                        console.error('El recorrido no pudo abrir lo siguiente', e);
                        terminar('saltado');
                        return;
                    }
                    // Las de antes se quedan (para poder volver), menos las que ya no están (el
                    // «día vacío» desaparece al crearlo); las de después se recuentan.
                    const relanzar = () => {
                        if (miVuelta !== vuelta) return;
                        const antes = pasos.slice(0, i + 1).filter((p) => p.dentro || !!visible(p.selector));
                        const resto = todas.slice(todas.indexOf(actual) + 1).filter(existe);
                        if (!resto.length) { terminar('visto'); return; }
                        lanzar(antes.concat(resto), antes.length);
                    };
                    if (hizo === false) { relanzar(); return; }
                    // SE ESPERA A QUE LO ABIERTO ESTÉ PINTADO, NO UN TIEMPO FIJO (25-09: en un
                    // teléfono de verdad la comida aún no se había abierto y se daba por
                    // terminado). Se mira cada poco hasta que las paradas siguientes dejan de
                    // cambiar (dos vueltas iguales), con un tope de diez segundos: los alimentos de
                    // una categoría tardaron casi cinco en salir (01-10) y con cuatro el recorrido
                    // se acababa sin enseñar el 80 y el 100.
                    const siguientes = () => todas.slice(todas.indexOf(actual) + 1).filter((p) => !p.sinElemento && !!visible(p.selector)).length;
                    const desde = Date.now();
                    let antes = -1, iguales = 0;
                    const mirar = () => {
                        if (miVuelta !== vuelta) return;
                        const n = siguientes();
                        iguales = n === antes ? iguales + 1 : 0;
                        antes = n;
                        const esperado = actual && actual.espera
                            && (typeof actual.espera === 'function' ? actual.espera() : !!visible(actual.espera));
                        const quieto = actual && actual.espera ? !!esperado : n > 0 && iguales >= 2;
                        if (!quieto && Date.now() - desde < 10000) { setTimeout(mirar, 200); return; }
                        relanzar();
                    };
                    setTimeout(mirar, 300);
                    return;
                }
                if (d.isLastStep()) { llegoAlFinal = true; d.destroy(); return; }
                d.moveNext();
            },
            // EL HUECO Y EL CUADRO, DONDE ESTÁ EL ELEMENTO (23-09: «marca algo que no es»).
            // driver.js mide al iluminar y no vuelve a medir si la página se mueve después (al
            // llevar el foco al botón, al terminar de pintarse algo): el marco naranja, que va
            // pegado al elemento, se quedaba bien, y el hueco y la flecha se quedaban abajo,
            // encima de otra cosa. Se vuelve a medir al iluminar y cada vez que algo se desplaza.
            onHighlighted: () => {
                requestAnimationFrame(() => { medirZona(); if (d.isActive()) d.refresh(); });
                setTimeout(() => { medirZona(); if (d.isActive()) d.refresh(); }, 250);
                setTimeout(() => { medirZona(); if (d.isActive()) d.refresh(); }, 900);
            },
            onDestroyed: () => {
                window.removeEventListener('scroll', recolocar, true);
                window.removeEventListener('resize', recolocar);
                if (window.visualViewport) window.visualViewport.removeEventListener('resize', recolocar);
                if (vigia) vigia.disconnect();
                if (recontando || parado) return;
                terminar(llegoAlFinal ? 'visto' : 'saltado');
            },
            steps: pasos.map((p, i) => ({
                // El elemento visible, no el primero del selector (ver `visible`). El cierre
                // (`sinElemento`) va sin elemento: driver.js lo pone en medio de la pantalla.
                element: p.sinElemento ? undefined : () => {
                    if (p.hasta) return zonaEntre(p.selector, p.hasta);
                    quitarZona();
                    let el = enMedio(p.selector);
                    for (let n = 0; n < (p.subir || 0) && el && el.parentElement && el !== document.body; n++) el = el.parentElement;
                    return el;
                },
                popover: {
                    title: p.titulo,
                    description: p.texto,
                    // En el ordenador «Más» es el menú de la izquierda: el cuadro, a su derecha.
                    // «Atrás» no tiene adónde ir en la primera.
                    disableButtons: i === 0 ? ['previous'] : [],
                    side: (p.selector || '').includes('desktop-sidebar') && window.matchMedia('(min-width: 1024px)').matches ? 'right' : p.side,
                    align: 'start',
                    // La que abre algo (`luego`) nunca es la última: detrás vienen las de lo abierto.
                    nextBtnText: i === pasos.length - 1 && !p.luego ? 'Listo' : 'Siguiente',
                },
            })),
        });
        const recolocar = () => { medirZona(); if (d.isActive()) d.refresh(); };
        window.addEventListener('scroll', recolocar, true);
        // EN SAFARI DEL IPAD Y EL IPHONE la barra de direcciones se recoge y se despliega al
        // desplazar: la pantalla cambia de alto sin que la página cambie. Se vuelve a medir.
        window.addEventListener('resize', recolocar);
        if (window.visualViewport) window.visualViewport.addEventListener('resize', recolocar);
        // Y si algo aparece o desaparece encima (un aviso que termina de cargar), la página
        // cambia de tamaño sin desplazarse: también se recoloca.
        const vigia = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(recolocar) : null;
        if (vigia) vigia.observe(document.body);
        enMarcha = { parar: () => { parado = true; terminado = true; if (d.isActive()) d.destroy(); } };
        d.drive(desde);
        // La capa de espera se va cuando ya está la de driver.js (que entra con un fundido), y
        // solo si este recorrido sigue en pantalla: si ya se pulsó otra vez, la nueva espera se queda.
        setTimeout(() => { if (d.isActive()) quitarEspera(); }, 250);
    };
    lanzar(primeros, 0);
    return true;
};
